/**
 * MapSelector - Componente per selezione posizione con mappa interattiva
 * Utilizza Leaflet + OpenStreetMap per evitare costi API
 */
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Map, Minimize2 } from 'lucide-react';
import { Button, Alert, Label, Input } from './ui';
import './MapSelector.css';

// Fix per le icone di Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const Wrapper = styled.div`
  margin: var(--space-md) 0;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-sm);
`;

const SelectorLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  font-size: var(--font-size-sm);

  svg {
    color: var(--primary-700);
  }
`;

const Instructions = styled.div`
  background: var(--info-soft);
  border-left: 4px solid var(--info);
  border-radius: var(--border-radius-sm);
  padding: var(--space-sm);
  font-size: var(--font-size-xs);
  color: var(--info);
  margin-bottom: var(--space-sm);
`;

const InstructionsDetail = styled.div`
  margin-top: var(--space-xs);
`;

const MapBox = styled.div<{ $expanded: boolean }>`
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  margin-bottom: var(--space-md);
  box-shadow: var(--shadow-sm);
  height: ${({ $expanded }) => ($expanded ? '400px' : '200px')};

  @media (max-width: 768px) {
    height: ${({ $expanded }) => ($expanded ? '350px' : '180px')};
  }
`;

const InfoBox = styled.div`
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-md);
  padding: var(--space-sm) var(--space-md);
  font-size: var(--font-size-sm);
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--space-sm);
  margin-bottom: var(--space-xs);

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
`;

const InfoValue = styled.span`
  color: var(--text-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-xs);
  text-align: right;
`;

const CoordinatesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-sm);
  margin-top: var(--space-sm);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CoordinateField = styled.div`
  display: flex;
  flex-direction: column;

  input {
    font-family: var(--font-family-mono);
  }
`;

const Controls = styled.div`
  margin-top: var(--space-sm);
  display: flex;
  gap: var(--space-sm);
  flex-wrap: wrap;
  padding: var(--space-sm);
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-md);
`;

const StatusWrap = styled.div`
  margin-top: var(--space-sm);
`;

export interface MapSelectorProps {
  latitude?: number | null;
  longitude?: number | null;
  address?: string;
  onLocationChange: (location: {
    latitude: number;
    longitude: number;
    address: string;
  }) => void;
  disabled?: boolean;
}

interface LocationClickHandlerProps {
  onClick: (lat: number, lng: number) => void;
}

// Componente per gestire i click sulla mappa
const LocationClickHandler: React.FC<LocationClickHandlerProps> = ({ onClick }) => {
  useMapEvents({
    click: (e) => {
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const MapSelector: React.FC<MapSelectorProps> = ({
  latitude,
  longitude,
  address = '',
  onLocationChange,
  disabled = false
}) => {
  const [position, setPosition] = useState<[number, number] | null>(() => {
    if (latitude && longitude && typeof latitude === 'number' && typeof longitude === 'number') {
      return [latitude, longitude];
    }
    return null;
  });
  const [currentAddress, setCurrentAddress] = useState(address);
  const [isExpanded, setIsExpanded] = useState(false);
  const [geocodingStatus, setGeocodingStatus] = useState<{
    type: 'loading' | 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  // Centro Italia di default (Roma)
  const defaultCenter: [number, number] = [41.9028, 12.4964];
  const mapCenter = position || defaultCenter;

  // Effetto per sincronizzare l'indirizzo dal parent (senza geocoding automatico)
  useEffect(() => {
    if (address !== currentAddress && !geocodingStatus.type) {
      setCurrentAddress(address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync solo quando cambia l'indirizzo dal parent; currentAddress come dipendenza annullerebbe le modifiche dell'utente
  }, [address]);

  // Effetto per aggiornare coordinate quando arrivano dal parent
  useEffect(() => {
    if (latitude && longitude && typeof latitude === 'number' && typeof longitude === 'number') {
      setPosition([latitude, longitude]);
    }
  }, [latitude, longitude]);

  // Geocoding inverso: coordinate → indirizzo
  const reverseGeocode = useCallback(async (lat: number, lng: number) => {
    try {
      setGeocodingStatus({ type: 'loading', message: 'Ricerca indirizzo...' });

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=it`
      );

      if (!response.ok) throw new Error('Errore nella richiesta');

      const data = await response.json();

      if (data && data.display_name) {
        const newAddress = data.display_name;
        setCurrentAddress(newAddress);
        setGeocodingStatus({
          type: 'success',
          message: 'Indirizzo trovato'
        });

        // Callback per aggiornare il parent
        onLocationChange({
          latitude: lat,
          longitude: lng,
          address: newAddress
        });
      } else {
        throw new Error('Indirizzo non trovato');
      }
    } catch (error) {
      console.error('Errore geocoding inverso:', error);
      setGeocodingStatus({
        type: 'error',
        message: 'Impossibile trovare l\'indirizzo'
      });
    }
  }, [onLocationChange]);

  // Handler per click sulla mappa
  const handleMapClick = useCallback((lat: number, lng: number) => {
    if (disabled) return;

    const newPosition: [number, number] = [lat, lng];
    setPosition(newPosition);
    reverseGeocode(lat, lng);
  }, [disabled, reverseGeocode]);

  // Handler per cambio coordinate manuali
  const handleCoordinateChange = useCallback((field: 'lat' | 'lng', value: string) => {
    if (disabled) return;

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    if (position) {
      const newPosition: [number, number] = field === 'lat'
        ? [numValue, position[1]]
        : [position[0], numValue];

      setPosition(newPosition);
      reverseGeocode(newPosition[0], newPosition[1]);
    }
  }, [disabled, position, reverseGeocode]);

  // Effetto per nascondere lo status dopo qualche secondo
  useEffect(() => {
    if (geocodingStatus.type) {
      const timer = setTimeout(() => {
        setGeocodingStatus({ type: null, message: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [geocodingStatus]);

  return (
    <Wrapper>
      <HeaderRow>
        <SelectorLabel>
          <MapPin size={16} />
          Posizione
        </SelectorLabel>
        <Button
          type="button"
          $variant="secondary"
          $size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          disabled={disabled}
        >
          {isExpanded ? <Minimize2 size={14} /> : <Map size={14} />}
          {isExpanded ? 'Comprimi' : 'Espandi Mappa'}
        </Button>
      </HeaderRow>

      <Instructions>
        <strong>Come impostare la posizione:</strong>
        <InstructionsDetail>
          • <strong>Metodo 1:</strong> Inserisci l'indirizzo sopra → Clicca "Cerca sulla Mappa"<br />
          • <strong>Metodo 2:</strong> Clicca direttamente sulla mappa per scegliere la posizione<br />
          • <strong>Aggiustamenti:</strong> Usa "Aggiusta Posizione" per modifiche precise<br />
          • Le coordinate precise garantiscono dati meteo accurati
        </InstructionsDetail>
      </Instructions>

      <MapBox $expanded={isExpanded}>
        <MapContainer
          center={mapCenter}
          zoom={position ? 16 : 6}
          style={{ height: '100%', width: '100%' }}
          key={`${mapCenter[0]}-${mapCenter[1]}`} // Force re-render quando cambiano le coordinate
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {!disabled && (
            <LocationClickHandler onClick={handleMapClick} />
          )}

          {position && (
            <Marker position={position}>
            </Marker>
          )}
        </MapContainer>
      </MapBox>

      {/* Informazioni posizione */}
      <InfoBox>
        <InfoItem>
          <InfoLabel>Indirizzo:</InfoLabel>
          <InfoValue>
            {currentAddress || 'Nessun indirizzo selezionato'}
          </InfoValue>
        </InfoItem>

        {position && Array.isArray(position) && position.length === 2 && (
          <InfoItem>
            <InfoLabel>Coordinate:</InfoLabel>
            <InfoValue>
              {typeof position[0] === 'number' ? position[0].toFixed(6) : '0.000000'}, {typeof position[1] === 'number' ? position[1].toFixed(6) : '0.000000'}
            </InfoValue>
          </InfoItem>
        )}
      </InfoBox>

      {/* Input coordinate manuali */}
      {position && !disabled && (
        <CoordinatesGrid>
          <CoordinateField>
            <Label>Latitudine</Label>
            <Input
              type="number"
              step="0.000001"
              value={position[0]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCoordinateChange('lat', e.target.value)}
              placeholder="es. 45.464200"
            />
          </CoordinateField>
          <CoordinateField>
            <Label>Longitudine</Label>
            <Input
              type="number"
              step="0.000001"
              value={position[1]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCoordinateChange('lng', e.target.value)}
              placeholder="es. 9.190000"
            />
          </CoordinateField>
        </CoordinatesGrid>
      )}

      {/* Status geocoding */}
      {geocodingStatus.type && (
        <StatusWrap>
          <Alert $variant={geocodingStatus.type === 'loading' ? 'info' : geocodingStatus.type}>
            {geocodingStatus.message}
          </Alert>
        </StatusWrap>
      )}

      {/* Controlli posizionamento */}
      {position && !disabled && (
        <Controls>
          <Button
            type="button"
            $variant="success"
            $size="sm"
            onClick={() => setIsExpanded(true)}
          >
            <MapPin size={14} />
            Aggiusta Posizione
          </Button>
        </Controls>
      )}
    </Wrapper>
  );
};

export default MapSelector;
